import inventoryText from "../inventoryI18n.js";
import React, { useState, useEffect, useContext } from "react";
import Modal from "react-bootstrap/Modal";
import EditSupplier from "./SupplierEdit";
import "./Suppliers.css";
import SupplierStatement from "./SupplierStatement";
import { API_URL } from "../../components/api";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
} from "@mui/x-data-grid";
import AuthContext from "../../context/AuthContext";
import ExcelSVG from "../ExcelSVG.svg";
import FilterSVG from "../FilterSVG.svg";
import * as XLSX from "xlsx";
import Button from "react-bootstrap/Button";

//For API Requests
import axios from "axios";

const getSupplierHistoryEmployeeName = (employee) => {
  if (!employee) {
    return "";
  }

  return employee.full_name || employee.username || "";
};

const formatSupplierHistoryDate = (value) => {
  if (!value) {
    return "";
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleString();
};

const getSupplierHistoryMeta = (event) => {
  const entries = [];

  const addEntry = (label, value) => {
    if (
      value !== null &&
      value !== undefined &&
      value !== ""
    ) {
      entries.push({
        label,
        value,
      });
    }
  };

  addEntry(
    "Performed by",
    getSupplierHistoryEmployeeName(event.actor)
  );

  if (event.purchase_request) {
    addEntry(
      "Request",
      event.purchase_request.request_number
    );

    addEntry(
      "Requested by",
      getSupplierHistoryEmployeeName(
        event.purchase_request.initiator
      )
    );

    addEntry(
      "Approved by",
      getSupplierHistoryEmployeeName(
        event.purchase_request.receiver
      )
    );
  }

  if (event.purchase_order) {
    addEntry(
      "Order",
      event.purchase_order.order_number
    );
  }

  if (event.goods_receipt) {
    addEntry(
      "Receipt",
      event.goods_receipt.receipt_number
    );
  }

  if (event.product) {
    addEntry("Product", event.product.name);
  }

  if (event.warehouse) {
    addEntry(
      "Warehouse",
      event.warehouse.warehouse_name
    );
  }

  if (event.amount_usd !== null) {
    addEntry("Amount", `$${event.amount_usd}`);
  }

  if (
    event.balance_before_usd !== null ||
    event.balance_after_usd !== null
  ) {
    addEntry(
      "Supplier balance",
      `$${event.balance_before_usd || "0.00"} → ` +
        `$${event.balance_after_usd || "0.00"}`
    );
  }

  if (event.quantity_delta !== null) {
    const quantityPrefix =
      event.quantity_delta > 0 ? "+" : "";

    let quantityValue =
      `${quantityPrefix}${event.quantity_delta}`;

    if (
      event.quantity_before !== null &&
      event.quantity_after !== null
    ) {
      quantityValue +=
        ` (${event.quantity_before} → ` +
        `${event.quantity_after})`;
    }

    addEntry("Quantity", quantityValue);
  }

  if (
    event.previous_status ||
    event.new_status
  ) {
    const statuses = [
      event.previous_status_label,
      event.new_status_label,
    ].filter(Boolean);

    addEntry("Status", statuses.join(" → "));
  }

  return entries;
};

function SupplierTable() {
  const [data, setData] = useState([]);
  const [showo, setShowo] = useState(false);
  const [dashoo, setDasho] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historySupplier, setHistorySupplier] = useState(null);
  const [historyEvents, setHistoryEvents] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize] = useState(20);
  const [historyTotalPages, setHistoryTotalPages] = useState(0);
  const [historyCount, setHistoryCount] = useState(0);

  const [loading, setLoading] = useState(true);

  const handleshowo = () => setShowo(true);
  const handleedit = () => setShowo(false);

  const showoDash = () => setDasho(true);
  const handledash = () => setDasho(false);

  const fetchSupplierHistory = async (
    supplier,
    requestedPage = 1
  ) => {
    if (!supplier || !supplier.supplier_id) {
      return;
    }

    setHistoryLoading(true);
    setHistoryError("");

    try {
      const storedTokens = JSON.parse(
        localStorage.getItem("authTokens")
      );

      const response = await axios.get(
        `${API_URL}/inventory/purchases/history/`,
        {
          headers: {
            Authorization:
              `Bearer ${storedTokens.access}`,
          },
          params: {
            supplier_id: supplier.supplier_id,
            page: requestedPage,
            page_size: historyPageSize,
          },
        }
      );

      const payload = response.data || {};
      const results = Array.isArray(payload.results)
        ? payload.results
        : [];

      setHistoryEvents(results);
      setHistoryCount(Number(payload.count) || 0);
      setHistoryPage(
        Number(payload.page) || requestedPage
      );
      setHistoryTotalPages(
        Number(payload.total_pages) || 0
      );
    } catch (error) {
      console.error(
        "Unable to load supplier history:",
        error
      );

      const responseData =
        error.response && error.response.data
          ? error.response.data
          : {};

      setHistoryEvents([]);
      setHistoryCount(0);
      setHistoryTotalPages(0);
      setHistoryError(
        responseData.error ||
          "Unable to load supplier history. " +
            "Please try again."
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleShowHistory = (supplier) => {
    setHistorySupplier(supplier);
    setHistoryEvents([]);
    setHistoryError("");
    setHistoryPage(1);
    setHistoryTotalPages(0);
    setHistoryCount(0);
    setShowHistory(true);

    fetchSupplierHistory(supplier, 1);
  };

  const handleCloseHistory = () => {
    setShowHistory(false);
    setHistorySupplier(null);
    setHistoryEvents([]);
    setHistoryError("");
    setHistoryPage(1);
    setHistoryTotalPages(0);
    setHistoryCount(0);
  };

  const handleHistoryPageChange = (
    requestedPage
  ) => {
    if (
      historyLoading ||
      !historySupplier ||
      requestedPage < 1 ||
      requestedPage > historyTotalPages
    ) {
      return;
    }

    fetchSupplierHistory(
      historySupplier,
      requestedPage
    );
  };

  const handleHistoryRetry = () => {
    if (!historySupplier || historyLoading) {
      return;
    }

    fetchSupplierHistory(
      historySupplier,
      historyPage
    );
  };

  const [searchQuery, setSearchQuery] = useState("");
  const { user } = useContext(AuthContext);
  let authTokens = JSON.parse(localStorage.getItem("authTokens"));
  const [
    canPrintStatements,
    setCanPrintStatements,
  ] = useState(false);
  const [
    statementSupplier,
    setStatementSupplier,
  ] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchStatementPermission = async () => {
      try {
        const storedTokens = JSON.parse(
          localStorage.getItem("authTokens") || "{}"
        );

        const response = await axios.get(
          `${API_URL}/inventory/purchases/permissions/`,
          {
            headers: {
              Authorization:
                `Bearer ${storedTokens.access}`,
            },
          }
        );

        if (isMounted) {
          setCanPrintStatements(
            Boolean(
              response.data &&
                response.data.can_approve_request
            )
          );
        }
      } catch (error) {
        console.error(
          "Unable to load statement permissions:",
          error
        );

        if (isMounted) {
          setCanPrintStatements(false);
        }
      }
    };

    fetchStatementPermission();

    return () => {
      isMounted = false;
    };
  }, []);

  const [DealerDetails, setDealerDetails] = useState({
    supplier_id: 0,
    firstname: "",
    lastname: "",
    company: "",
    info_id: 0,
    phone: "",
  });

  const [filters, setFilters] = useState({
    fullname_en: "",
    phone: "",
    company: "",
    balance_usd: "",
  });
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [gridFilterModel, setGridFilterModel] = useState({
    items: [],
  });

  const [searchInput, setSearchInput] = useState(""); // To capture search input

  // NEW: State to track column visibility
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [searchFilterTimeout, setSearchFilterTimeout] = useState(null);

  const updateDealerDetails = (details) => {
    setDealerDetails(details);
  };
  useEffect(() => {
    const fetchData = async () => {
      try {
        const authTokens = JSON.parse(localStorage.getItem("authTokens"));
        const response = await axios.get(`${API_URL}/inventory/suppliers/`, {
          headers: {
            Authorization: `Bearer ${authTokens.access}`,
          },
          params: {
            username: user.username,
          },
        });

        if (response.status === 200) {
          setData(response.data);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user.username]);

  const columns = [
    { field: "fullname_en", headerName: inventoryText("inventory.FullName"), flex: 2, minWidth: 150 },
    {
      field: "phone_number",
      headerName: inventoryText("inventory.Phone"),
      flex: 1,
      minWidth: 80,
    },
    {
      field: "company_name",
      headerName: inventoryText("inventory.Company"),
      flex: 1,
      minWidth: 80,
    },
    {
      field: "balance_usd",
      headerName: inventoryText("inventory.BalanceUsd"),
      flex: 1,
      minWidth: 80,
    },
    {
      field: "actions",
      headerName: inventoryText("inventory.Actions"),
      minWidth: 270,
      flex: 2,
      renderCell: (params) => (
        <div className="supplier-row-actions">
          <button
            type="button"
            className="supplier-row-action"
            onClick={() => {
              const details = {
                supplier_id: params.row.supplier_id,
                firstname: params.row.first_name,
                lastname: params.row.last_name,
                company: params.row.company_name,
                info_id: params.row.info_id,
                phone: params.row.phone_number,
              };
              updateDealerDetails(details);
              handleshowo();
            }}
          >{inventoryText("inventory.Edit")}</button>
          <button
            type="button"
            className="supplier-row-action"
            onClick={() =>
              handleShowHistory({
                supplier_id: params.row.supplier_id,
                company_name: params.row.company_name,
                fullname_en: params.row.fullname_en,
              })
            }
          >{inventoryText("inventory.History")}</button>
          {canPrintStatements && (
            <button
              type="button"
              className="supplier-row-action"
              onClick={() =>
                setStatementSupplier({
                  supplier_id:
                    params.row.supplier_id,
                  company_name:
                    params.row.company_name,
                  fullname_en:
                    params.row.fullname_en,
                })
              }
            >{inventoryText("inventory.Statement")}</button>
          )}
        </div>
      ),
    },
  ];

  const getRowId = (row) => row.supplier_id;

  // Create a delayed search filter function
  const delaySearchFilter = (input) => {
    setSearchQuery(input);
  };

  useEffect(() => {
    // Save "searchValue" value before clearing other session storage items
    const searchValue = sessionStorage.getItem("searchSupplier");

    // Clear all session storage items except "searchValue"
    Object.keys(sessionStorage).forEach((key) => {
      if (key !== "searchSupplier") {
        sessionStorage.removeItem(key);
      }
    });

    // Restore "searchValue" value if needed
    if (searchValue) {
      sessionStorage.setItem("searchSupplier", searchValue);
      setSearchQuery(searchValue);
      setSearchInput(searchValue);
    }
  }, []);

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchInput(query);
    sessionStorage.setItem("searchSupplier", query);

    if (searchFilterTimeout) {
      clearTimeout(searchFilterTimeout);
    }

    const newTimeout = setTimeout(() => {
      delaySearchFilter(query);
    }, 100);
    setSearchFilterTimeout(newTimeout);
  };

  // IMPROVED: Combined filtering logic that works for both display AND export
  const filteredRows = data.filter((row) => {
    // Apply search query filter
    const searchMatch = searchQuery
      .split(" ")
      .every((word) =>
        Object.values(row).some((value) =>
          String(value).toLowerCase().includes(word.toLowerCase().trim())
        )
      );

    // Apply modal filters
    const modalFilterMatch = Object.keys(filters).every((key) => {
      if (!filters[key]) return true;

      if (key === "connectivity_dependent") {
        const filterValue = filters[key].toLowerCase();
        if (row.connectivity === "Fiber") {
          return (row.code || "").toLowerCase().includes(filterValue);
        } else if (row.connectivity === "Wireless") {
          return (row.name || "").toLowerCase().includes(filterValue);
        }
        return false;
      }

      const rowValue = String(row[key] || "").toLowerCase();
      const filterValue = String(filters[key]).toLowerCase();
      return rowValue.includes(filterValue);
    });

    // Apply DataGrid filters
    const gridFilterMatch = gridFilterModel.items.every((filterItem) => {
      if (!filterItem.value) return true;

      const cellValue = row[filterItem.field];
      const filterValue = filterItem.value;

      switch (filterItem.operator) {
        case "contains":
          return String(cellValue || "")
            .toLowerCase()
            .includes(String(filterValue).toLowerCase());
        case "equals":
          return String(cellValue || "") === String(filterValue);
        case "startsWith":
          return String(cellValue || "")
            .toLowerCase()
            .startsWith(String(filterValue).toLowerCase());
        case "endsWith":
          return String(cellValue || "")
            .toLowerCase()
            .endsWith(String(filterValue).toLowerCase());
        default:
          return true;
      }
    });

    return searchMatch && modalFilterMatch && gridFilterMatch;
  });

  const handleFilterChange = (field, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [field]: value,
    }));
  };

  // Define the column headers mapping
  const columnHeaders = {
    fullname_en: "Full Name",
    phone_number: "Phone",
    company_name: "Company",
  };

  // Format date fields properly for export — e.g. "21/02/2025 10:25:52 AM"
  const formatDateForExport = (dateValue) => {
    if (!dateValue) return "";
    const date = new Date(dateValue);

    // Format as DD/MM/YYYY HH:MM:SS AM/PM
    const formattedDate = date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });

    // Ensure consistent spacing before AM/PM
    return formattedDate.replace(" am", " AM").replace(" pm", " PM");
  };

  /// Corrected: Transform rows respecting column visibility
  const transformRowsForExport = (rows) => {
    return rows.map((row) => {
      const transformedRow = {};

      Object.keys(columnHeaders).forEach((field) => {
        // Only export if column is visible
        if (columnVisibilityModel[field] !== false) {
          const headerName = columnHeaders[field];

          // Default: use the value or empty string
          transformedRow[headerName] = row[field] ?? "";
        }
      });

      return transformedRow;
    });
  };

  // IMPROVED: Export function that uses the already-filtered rows
  const handleExportToExcel = () => {
    console.log("Exporting data...");
    console.log("Total data rows:", data.length);
    console.log("Filtered rows:", filteredRows.length);
    console.log("Active filters:", {
      search: searchQuery,
      modalFilters: filters,
      gridFilters: gridFilterModel.items,
    });
    console.log("Column visibility:", columnVisibilityModel);

    // Always use filteredRows which has ALL filters applied
    const transformedRows = transformRowsForExport(filteredRows);

    console.log("Rows to export:", transformedRows.length);

    const worksheet = XLSX.utils.json_to_sheet(transformedRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Suppliers");
    XLSX.writeFile(workbook, "Suppliers.xlsx");
  };

  const [isPressed, setIsPressed] = useState(false);

  const handleExportClick = () => {
    setIsPressed(true);
    handleExportToExcel();
    setTimeout(() => {
      setIsPressed(false);
    }, 200);
  };

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <Button
        onClick={() => setShowFilterModal(true)}
        variant="outline-primary"
        style={{
          border: "none",
        }}
      >
        <img
          src={FilterSVG}
          alt="Filter Icon"
          style={{
            color: "white",
            width: "18px",
            marginRight: "8px",
            filter: "invert(0)", // keep black icon
          }}
        />{inventoryText("inventory.Filter")}</Button>
      {/*<GridToolbarFilterButton />*/}
      <GridToolbarExport />
      <Button
        onClick={handleExportClick}
        variant="outline-success"
        style={{
          display: "flex",
          alignItems: "center",
          padding: "5px 10px",
          fontSize: "14px",
          border: "none",
          backgroundColor: isPressed ? "white" : "transparent",
          color: isPressed ? "black" : "#007bff",
          cursor: "pointer",
        }}
      >
        <img
          src={ExcelSVG}
          alt="Excel Icon"
          style={{
            width: "20px",
            marginRight: "8px",
            filter: isPressed ? "invert(0)" : "invert(1)",
          }}
        />
        Excel
      </Button>
    </GridToolbarContainer>
  );

  //Datatable HTML
  return (
    <div className="ClientTable supplier-table">
      <style>{`
        .MuiDataGrid-panelWrapper {
          background: #122632 !important;
        }
      `}</style>
      <input
        type="text"
        placeholder={inventoryText("inventory.Search")}
        value={searchInput}
        onChange={handleSearchChange}
        className="search-client"
      />
      {!loading && data.length > 0 && (
        <div style={{ height: 520, width: "100%" }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            getRowId={getRowId}
            filterModel={gridFilterModel}
            onFilterModelChange={(newModel) => setGridFilterModel(newModel)}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) =>
              setColumnVisibilityModel(newModel)
            }
            components={{
              Toolbar: CustomToolbar,
            }}
            rowsPerPageOptions={[10, 100, 1000]}
            pagination
            disableSelectionOnClick
          />
        </div>
      )}
      <Modal
        show={showo}
        onHide={handleedit}
        className="supplier-edit-modal supplier-themed-modal"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{inventoryText("inventory.EditSupplier")}</Modal.Title>
        </Modal.Header>
        <EditSupplier
          supplier_id={DealerDetails.supplier_id}
          firstname={DealerDetails.firstname}
          lastname={DealerDetails.lastname}
          company={DealerDetails.company}
          info_id={DealerDetails.info_id}
          phone={DealerDetails.phone}
        />
      </Modal>
      <SupplierStatement
        show={Boolean(statementSupplier)}
        supplier={statementSupplier}
        apiUrl={API_URL}
        onClose={() =>
          setStatementSupplier(null)
        }
      />

      <Modal
        show={showHistory}
        onHide={handleCloseHistory}
        className="supplier-history-modal supplier-themed-modal"
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {inventoryText("inventory.SupplierHistory")}
            {historySupplier && historySupplier.company_name
              ? ` — ${historySupplier.company_name}`
              : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {historyLoading ? (
            <div
              className="supplier-history-state supplier-history-loading"
              role="status"
              aria-live="polite"
            >
              <span
                className="supplier-history-spinner"
                aria-hidden="true"
              />
              <h3>{inventoryText("inventory.LoadingSupplierHistory")}</h3>
              <p>
                Retrieving recorded purchase and inventory activity.
              </p>
            </div>
          ) : historyError ? (
            <div
              className="supplier-history-state supplier-history-error"
              role="alert"
            >
              <h3>{inventoryText("inventory.HistoryCouldNotBeLoaded")}</h3>
              <p>{historyError}</p>
              <button
                type="button"
                className="supplier-modal-secondary"
                onClick={handleHistoryRetry}
              >{inventoryText("inventory.TryAgain")}</button>
            </div>
          ) : historyEvents.length === 0 ? (
            <div className="supplier-history-state supplier-history-empty">
              <h3>{inventoryText("inventory.NoSupplierHistoryYet")}</h3>
              <p>
                There is no recorded purchase or inventory activity for{" "}
                <strong>
                  {historySupplier
                    ? historySupplier.company_name ||
                      historySupplier.fullname_en
                    : "this supplier"}
                </strong>
                .
              </p>
            </div>
          ) : (
            <>
              <div className="supplier-history-summary">
                <span>
                  {historyCount}{" "}
                  {historyCount === 1
                    ? "recorded event"
                    : "recorded events"}
                </span>
                <span>{inventoryText("inventory.NewestActivityFirst")}</span>
              </div>

              <ol className="supplier-history-timeline">
                {historyEvents.map((event) => {
                  const metaItems =
                    getSupplierHistoryMeta(event);

                  return (
                    <li
                      key={
                        event.inventory_audit_event_id
                      }
                      className="supplier-history-event"
                    >
                      <span
                        className="supplier-history-marker"
                        aria-hidden="true"
                      />

                      <article className="supplier-history-card">
                        <div className="supplier-history-card-header">
                          <span className="supplier-history-event-label">
                            {event.event_label}
                          </span>
                          <time
                            dateTime={event.creation_date}
                            className="supplier-history-date"
                          >
                            {formatSupplierHistoryDate(
                              event.creation_date
                            )}
                          </time>
                        </div>

                        <p className="supplier-history-description">
                          {event.description}
                        </p>

                        {metaItems.length > 0 && (
                          <dl className="supplier-history-meta">
                            {metaItems.map(
                              (item, index) => (
                                <div
                                  key={
                                    `${item.label}-${index}`
                                  }
                                  className="supplier-history-meta-item"
                                >
                                  <dt>{item.label}</dt>
                                  <dd>{item.value}</dd>
                                </div>
                              )
                            )}
                          </dl>
                        )}
                      </article>
                    </li>
                  );
                })}
              </ol>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="supplier-history-pagination">
            {historyTotalPages > 1 && (
              <>
                <button
                  type="button"
                  className="supplier-modal-secondary"
                  disabled={
                    historyLoading ||
                    historyPage <= 1
                  }
                  onClick={() =>
                    handleHistoryPageChange(
                      historyPage - 1
                    )
                  }
                >{inventoryText("inventory.Previous")}</button>

                <span>
                  Page {historyPage} of{" "}
                  {historyTotalPages}
                </span>

                <button
                  type="button"
                  className="supplier-modal-secondary"
                  disabled={
                    historyLoading ||
                    historyPage >=
                      historyTotalPages
                  }
                  onClick={() =>
                    handleHistoryPageChange(
                      historyPage + 1
                    )
                  }
                >{inventoryText("inventory.Next")}</button>
              </>
            )}
          </div>

          <button
            type="button"
            className="supplier-modal-secondary"
            onClick={handleCloseHistory}
          >{inventoryText("inventory.Close")}</button>
        </Modal.Footer>
      </Modal>
      <Modal show={showFilterModal} onHide={() => setShowFilterModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{inventoryText("inventory.Filters")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {columns
            .filter((col) => col.field !== "actions")
            .map((col) => (
              <div key={col.field} className="filter">
                <label>{col.headerName}</label>
                <input
                  type="text"
                  placeholder={`Filter by ${col.headerName}`}
                  value={filters[col.field] || ""}
                  onChange={(e) =>
                    handleFilterChange(col.field, e.target.value)
                  }
                  className="filter-input"
                />
              </div>
            ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFilterModal(false)}>{inventoryText("inventory.Close")}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default SupplierTable;
